import { useMemo, useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { CopyId } from 'components/ui/copy-id';
import { Timestamp } from 'components/ui/timestamp';
import { Typography } from 'components/ui/typography';

import { Section, SectionContainer, SectionContent, SectionHeader } from './style';
import { CostCard } from '../Cost';
import {
  CheckoutIntentItem,
  CheckoutIntentLineItem,
  StateBadge,
} from 'pages/CheckoutIntents/column';
import {
  CommissionItem,
  useCheckoutIntentCommissions,
} from 'pages/Commissions/hooks/useCommissions';
import { formatPrice } from 'utils/formatters';
import { Link } from 'react-router-dom';
import { ShippingCard } from '../Shipping';
import { TrackingEventsCard } from '../TrackingEvents';
import { ReturnsCard } from '../Returns';
import { RequestReturnModal } from '../RequestReturnModal';
import { useShipments } from '../../hooks/useShipments';
import { useReturns } from '../../hooks/useReturns';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { canRead, canWrite } from 'app/access';

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  item?: CheckoutIntentItem;
};

// Two-column label/value row. 96px column fits all the labels we use here
// ("Created", "Buyer", "Items", etc.) without wasting horizontal real
// estate on the value side. Items in the value column align by baseline so
// long lists / multi-row content read straight down the column. The value
// column is locked to text-sm (14px) so heterogeneous children — Timestamp,
// CopyId, link, plain — all share one baseline; child primitives inherit
// the size unless we explicitly override.
const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-[96px_1fr] items-baseline gap-3">
    <Typography variant="label" as="div">
      {label}
    </Typography>
    <div className="text-sm leading-6 text-[var(--text)]">{children}</div>
  </div>
);

const formatMoney = (m: { amountSubunits: number; currencyCode: string }) =>
  formatPrice(m.amountSubunits / 100, m.currencyCode);

const CommissionRow = ({ commission }: { commission: CommissionItem }) => (
  <div className="flex items-center justify-between border-b py-2 last:border-b-0">
    <div className="flex items-center gap-2">
      <Link to={`/commissions/${commission.id}`} className="font-mono text-sm underline">
        {commission.id}
      </Link>
      <span className="text-xs capitalize text-muted-foreground">{commission.status}</span>
    </div>
    <span className="text-sm text-green-600">{formatMoney(commission.developerCommission)}</span>
  </div>
);

const LineItemsList = ({ items }: { items: CheckoutIntentLineItem[] }) => {
  const isMultiItem = items.length > 1;
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((lineItem, index) => (
        <li key={`${lineItem.productUrl}-${index}`} className="flex items-baseline gap-2">
          {isMultiItem && (
            <Typography variant="mono" as="span" className="shrink-0 tabular-nums">
              {index + 1}.
            </Typography>
          )}
          <Typography variant="link" asChild>
            <a
              href={lineItem.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate"
            >
              {lineItem.productUrl}
            </a>
          </Typography>
          {isMultiItem && (
            <Typography variant="small" as="span" className="shrink-0">
              qty {lineItem.quantity}
              {lineItem.status ? ` · ${lineItem.status}` : ''}
            </Typography>
          )}
        </li>
      ))}
    </ul>
  );
};

const OrderIdsList = ({ orderIds }: { orderIds: string[] }) => (
  <ul className="flex flex-col gap-1">
    {orderIds.map((orderId, index) => (
      <li key={orderId} className="flex items-baseline gap-2">
        {orderIds.length > 1 && (
          <Typography variant="mono" as="span" className="shrink-0 tabular-nums">
            {index + 1}.
          </Typography>
        )}
        <CopyId
          value={orderId}
          truncateAt={Number.POSITIVE_INFINITY}
          toastMessage="Order ID copied"
          className="-ml-1.5 text-sm"
        />
      </li>
    ))}
  </ul>
);

export const ActivityDetailSheet = ({ open, onOpenChange, item }: Props) => {
  const userInfo = useAppSelector(selectUserInfo);
  const canRequestReturns = canWrite(userInfo, 'checkout_intents');
  const canViewCommissions = canRead(userInfo, 'billing');
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const { shipments, loading: shipmentsLoading, error: shipmentsError } = useShipments(item?.id);
  const {
    returns,
    loading: returnsLoading,
    error: returnsError,
    refetch: refetchReturns,
  } = useReturns(item?.id);
  const { commissions } = useCheckoutIntentCommissions(item?.id, canViewCommissions);

  const isAmazon = useMemo(
    () =>
      /^https?:\/\/(www\.)?((amazon\.(com|co\.uk|ca|de|fr|es|it|co\.jp|com\.au|in|com\.br|nl|sg|com\.mx|ae))|(a\.co))(\/|$)/i.test(
        item?.productUrl ?? '',
      ),
    [item?.productUrl],
  );

  const buyerName = item ? `${item.buyerFirstName} ${item.buyerLastName}`.trim() : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-6/12 overflow-y-auto">
        <SheetHeader className="space-y-4">
          {/* Top action row — state on the left, secondary actions right.
              Reads as a status banner rather than a misused heading. */}
          <div className="flex items-center justify-between gap-3">
            {item && (
              <StateBadge
                stateRaw={item.stateRaw}
                label={item.state}
                failureCode={item.failureCode}
              />
            )}
            {isAmazon && canRequestReturns && (
              <Button variant="outline" size="sm" onClick={() => setReturnModalOpen(true)}>
                Request Return
              </Button>
            )}
          </div>

          {/* Identity block — the intent ID is the title here (CopyId
              renders mono and copyable). Radix needs a SheetTitle for a11y;
              we wrap the CopyId so the ID itself is the announced label,
              and add an sr-only description for screen readers. */}
          {item?.id && (
            <SheetTitle asChild>
              <CopyId
                value={item.id}
                truncateAt={Number.POSITIVE_INFINITY}
                toastMessage="Intent ID copied"
                className="-ml-1.5 text-base text-[var(--text)]"
              />
            </SheetTitle>
          )}
          <SheetDescription className="sr-only">
            Details, cost breakdown, shipping, and tracking for checkout intent {item?.id ?? ''}.
          </SheetDescription>

          {item?.failReason && (
            <div className="border-l-2 border-[var(--rye-red)] bg-[rgba(255,101,104,0.08)] px-3 py-2.5">
              <Typography variant="label" as="div" className="text-[var(--rye-red)]">
                Failure reason
              </Typography>
              <Typography variant="body" as="div" className="mt-1 text-[var(--text)]">
                {item.failReason}
              </Typography>
            </div>
          )}

          <div className="space-y-2 pt-1">
            {item?.createdAt && (
              <DetailRow label="Created">
                <Timestamp date={item.createdAt} className="text-sm" />
              </DetailRow>
            )}
            {item?.items && item.items.length > 0 && (
              <DetailRow label="Items">
                <LineItemsList items={item.items} />
              </DetailRow>
            )}
            {item?.orderIds && item.orderIds.length > 0 && (
              <DetailRow label="Orders">
                <OrderIdsList orderIds={item.orderIds} />
              </DetailRow>
            )}
            {buyerName && <DetailRow label="Buyer">{buyerName}</DetailRow>}
            {item?.buyerEmail && (
              <DetailRow label="Email">
                <Typography variant="mono" as="span">
                  {item.buyerEmail}
                </Typography>
              </DetailRow>
            )}
          </div>
        </SheetHeader>

        <SectionContainer>
          {item?.buyer && (
            <Section>
              <SectionHeader>
                <Typography variant="eyebrow" as="h3">
                  Shipping details
                </Typography>
              </SectionHeader>
              <SectionContent>
                <ShippingCard buyer={item.buyer} />
              </SectionContent>
            </Section>
          )}

          {/* Cost is only meaningful once an offer was retrieved — skip
              the section entirely for failed/expired/retrieving intents
              where every figure is a $0.00 placeholder. */}
          {item?.cost && item.cost.totalCents > 0 && (
            <Section>
              <SectionHeader>
                <Typography variant="eyebrow" as="h3">
                  Cost breakdown · {item.cost.currency}
                </Typography>
              </SectionHeader>
              <SectionContent>
                <CostCard
                  cost={{
                    totalCents: item.cost.totalCents,
                    subtotalCents: item.cost.subtotalCents,
                    shippingCents: item.cost.shippingCents,
                    taxCents: item.cost.taxCents,
                    discountCents: item.cost.discountCents,
                    surchargeCents: item.cost.surchargeCents,
                    currency: item.cost.currency,
                  }}
                />
              </SectionContent>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <Typography variant="eyebrow" as="h3">
                Tracking events
              </Typography>
            </SectionHeader>
            <SectionContent>
              <TrackingEventsCard
                shipments={shipments}
                loading={shipmentsLoading}
                error={shipmentsError}
                estimatedDeliveryDate={item?.estimatedDeliveryDate}
              />
            </SectionContent>
          </Section>

          {commissions.length > 0 && (
            <Section>
              <SectionHeader>
                <SheetTitle>Commissions</SheetTitle>
              </SectionHeader>
              <SectionContent>
                <div className="rounded-lg border px-3">
                  {commissions.map((c) => (
                    <CommissionRow key={c.id} commission={c} />
                  ))}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium">Total</span>
                    <span className="text-sm font-medium text-green-600">
                      {formatMoney({
                        amountSubunits: commissions.reduce(
                          (sum, c) => sum + c.developerCommission.amountSubunits,
                          0,
                        ),
                        currencyCode: commissions[0].developerCommission.currencyCode,
                      })}
                    </span>
                  </div>
                </div>
              </SectionContent>
            </Section>
          )}

          {isAmazon && (
            <Section>
              <SectionHeader>
                <Typography variant="eyebrow" as="h3">
                  Returns
                </Typography>
              </SectionHeader>
              <SectionContent>
                <ReturnsCard returns={returns} loading={returnsLoading} error={returnsError} />
              </SectionContent>
            </Section>
          )}
        </SectionContainer>
      </SheetContent>
      {item?.id && canRequestReturns && (
        <RequestReturnModal
          open={returnModalOpen}
          onOpenChange={setReturnModalOpen}
          checkoutIntentId={item.id}
          orderIds={item.orderIds}
          onSuccess={refetchReturns}
        />
      )}
    </Sheet>
  );
};
